import { ChangeEvent, useRef } from 'react';
import { Avatar, Button, Icon } from '@kontentino/ui';
import { faCamera } from '@fortawesome/pro-regular-svg-icons/faCamera';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons/faTrashCan';
import { useTranslation } from 'react-i18next';

/** The types the api's logo upload requests accept (`mimes:jpeg,jpg,png,gif,webp`). */
export const LOGO_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

interface LogoUploadFieldProps {
  name: string;
  previewUrl: string | null;
  onFilePick: (file: File) => void;
  onRemove: () => void;
  canRemove: boolean;
  isBusy?: boolean;
  isReadOnly?: boolean;
  hint?: string;
  dataCy?: string;
}

const LogoUploadField = ({
  name,
  previewUrl,
  onFilePick,
  onRemove,
  canRemove,
  isBusy = false,
  isReadOnly = false,
  hint,
  dataCy = 'logo-upload-field',
}: LogoUploadFieldProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (file) onFilePick(file);
  };

  return (
    <div className="tw-flex tw-items-center tw-gap-4" data-cy={dataCy}>
      <Avatar size={64} src={previewUrl ?? undefined} name={name} />
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => inputRef.current?.click()}
            iconBefore={<Icon icon={faCamera} />}
            disabled={isReadOnly || isBusy}
            data-cy={`${dataCy}_pick`}
          >
            {previewUrl
              ? t('logoUploadField.replace')
              : t('logoUploadField.upload')}
          </Button>
          {canRemove && !isReadOnly && (
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={onRemove}
              iconBefore={<Icon icon={faTrashCan} />}
              disabled={isBusy}
              data-cy={`${dataCy}_remove`}
            >
              {t('delete')}
            </Button>
          )}
        </div>
        {hint && (
          <span className="tw-text-xs tw-text-grayscale-100">{hint}</span>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={LOGO_MIME_TYPES.join(',')}
        onChange={handleChange}
        className="tw-hidden"
        data-cy={`${dataCy}_input`}
      />
    </div>
  );
};

export default LogoUploadField;
